import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, NAV_LINKS } from "../data/site";

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled || open
            ? "bg-ink/85 backdrop-blur-xl border-b border-white/10"
            : "bg-transparent border-b border-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-home-link">
            <img src="/logo.png" alt="MAXIMUS TREADS logo" className="h-10 w-10 object-contain" data-testid="nav-logo" />
            <span className="leading-none">
              <span className="block font-display font-extrabold text-xl tracking-wide text-white">MAXIMUS</span>
              <span className="block font-mono text-[9px] tracking-[0.45em] text-slate-400 mt-0.5">TREADS</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative font-mono text-[11px] uppercase tracking-[0.25em] transition-colors py-2 ${
                    isActive ? "text-white" : "text-slate-400 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && <span className="absolute left-0 -bottom-0.5 h-[2px] w-full bg-amber" />}
                  </>
                )}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-cta-request-quote"
              className="group relative overflow-hidden bg-signal px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white hover:bg-signalhover transition-colors"
            >
              Request a Quote
            </Link>
          </nav>

          <button
            className="md:hidden text-white p-2 -mr-2"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-ink blueprint-grid md:hidden pt-24"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 pt-6 flex flex-col gap-2">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `block font-display text-5xl font-extrabold uppercase tracking-tight py-2 ${
                        isActive ? "text-amber" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.45 }}
                className="mt-10 border-t border-white/10 pt-6 space-y-3"
              >
                {CONTACT.phones.map((p) => (
                  <a key={p} href={`tel:${p}`} className="flex items-center gap-3 text-slate-300 font-mono text-sm" data-testid={`nav-mobile-phone-${p}`}>
                    <Phone size={14} className="text-amber" /> {p}
                  </a>
                ))}
                <a href={`mailto:${CONTACT.email}`} className="block text-slate-300 font-mono text-sm break-all" data-testid="nav-mobile-email">
                  {CONTACT.email}
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
